import type { ReactNode } from 'react'
import type { ProductImage } from '@/types'

import { useCallback, useEffect, useRef } from 'react'
import Image from 'next/image'
import { ArrowLeft, ArrowRight, X } from 'lucide-react'

import { cn } from '@/lib/utils'
import { gsap, registerMotion, prefersReducedMotion } from '@/lib/motion'

interface ProductLightboxProps {
    images: ProductImage[]
    index: number
    isOpen: boolean
    onClose: () => void
    onIndexChange: (index: number) => void
}

/**
 * Full-screen viewer for the product imagery. Opens from the gallery, supports
 * arrow keys and Escape, and restores focus to whatever opened it.
 */
export const ProductLightbox = ({
    images,
    index,
    isOpen,
    onClose,
    onIndexChange
}: ProductLightboxProps): ReactNode => {
    const overlay = useRef<HTMLDivElement | null>(null)
    const figure = useRef<HTMLDivElement | null>(null)
    const closeButton = useRef<HTMLButtonElement | null>(null)
    const restoreFocus = useRef<HTMLElement | null>(null)
    const previousIndex = useRef(index)
    const mounted = useRef(false)

    const go = useCallback(
        (direction: 1 | -1) => {
            onIndexChange((index + direction + images.length) % images.length)
        },
        [index, images.length, onIndexChange]
    )

    // Open and close the shell.
    useEffect(() => {
        const shade = overlay.current

        if (!shade) return

        registerMotion()

        const reduced = prefersReducedMotion()
        const firstRun = !mounted.current

        mounted.current = true

        // Nothing to close on the very first render; the viewer starts hidden.
        if (!isOpen && firstRun) return

        const tl = gsap.timeline()

        if (isOpen) {
            restoreFocus.current = document.activeElement as HTMLElement | null

            const previousOverflow = document.body.style.overflow

            document.body.style.overflow = 'hidden'

            tl.set(shade, { display: 'flex' })
                .fromTo(
                    shade,
                    { opacity: 0 },
                    { opacity: 1, duration: reduced ? 0 : 0.35, ease: 'nuvea' }
                )
                .fromTo(
                    shade.querySelectorAll<HTMLElement>('[data-lightbox-chrome]'),
                    { y: 14, opacity: 0 },
                    { y: 0, opacity: 1, duration: reduced ? 0 : 0.45, stagger: 0.05, ease: 'nuvea' },
                    reduced ? 0 : '-=0.15'
                )

            closeButton.current?.focus({ preventScroll: true })

            return () => {
                document.body.style.overflow = previousOverflow
                tl.kill()
            }
        }

        tl.to(shade, { opacity: 0, duration: reduced ? 0 : 0.3, ease: 'nuvea' })
            .set(shade, { display: 'none' })

        restoreFocus.current?.focus({ preventScroll: true })

        return () => {
            tl.kill()
        }
    }, [isOpen])

    // Keyboard control while open.
    useEffect(() => {
        if (!isOpen) return

        const onKey = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') {
                event.preventDefault()
                onClose()
            }

            if (event.key === 'ArrowRight') {
                event.preventDefault()
                go(1)
            }

            if (event.key === 'ArrowLeft') {
                event.preventDefault()
                go(-1)
            }
        }

        window.addEventListener('keydown', onKey)

        return () => window.removeEventListener('keydown', onKey)
    }, [isOpen, onClose, go])

    // Slide the frame in the direction of travel on every change.
    useEffect(() => {
        const node = figure.current

        if (!node || !isOpen) return

        registerMotion()

        if (prefersReducedMotion()) return

        const forward = index >= previousIndex.current

        previousIndex.current = index

        const tween = gsap.fromTo(
            node,
            { opacity: 0, x: forward ? 40 : -40, scale: 0.98 },
            { opacity: 1, x: 0, scale: 1, duration: 0.55, ease: 'nuvea', overwrite: true }
        )

        return () => {
            tween.kill()
        }
    }, [index, isOpen])

    const current = images[index]

    return (
        <div
            ref={overlay}
            role='dialog'
            aria-modal='true'
            aria-label='Product images'
            className='fixed inset-0 z-[75] hidden flex-col bg-cream/97 opacity-0 backdrop-blur-sm'
        >
            <div
                data-lightbox-chrome=''
                className='shell-x flex shrink-0 items-center justify-between py-4'
            >
                <p className='type-small text-slate-mute tabular-nums'>
                    {index + 1} / {images.length}
                </p>

                <button
                    ref={closeButton}
                    type='button'
                    onClick={onClose}
                    tabIndex={isOpen ? 0 : -1}
                    aria-label='Close image viewer'
                    className='flex size-10 cursor-pointer items-center justify-center rounded-full border border-stone-line text-ink transition-colors duration-400 ease-nuvea hover:border-ink hover:bg-ink hover:text-cream'
                >
                    <X className='size-4' strokeWidth={1.5} />
                </button>
            </div>

            <div className='relative flex min-h-0 flex-1 items-center justify-center px-4 pb-4 md:px-16'>
                <button
                    type='button'
                    onClick={() => go(-1)}
                    tabIndex={isOpen ? 0 : -1}
                    aria-label='Previous image'
                    data-lightbox-chrome=''
                    className='absolute left-2 z-10 flex size-11 cursor-pointer items-center justify-center rounded-full border border-stone-line bg-cream/80 text-ink transition-colors duration-400 ease-nuvea hover:border-ink hover:bg-ink hover:text-cream md:left-4'
                >
                    <ArrowLeft className='size-4' strokeWidth={1.6} />
                </button>

                <div ref={figure} className='relative size-full'>
                    {current && (
                        <Image
                            src={current.src}
                            alt={current.alt}
                            fill
                            sizes='100vw'
                            className={cn(
                                'rounded-2xl',
                                current.fit === 'cover' ? 'object-cover' : 'object-contain'
                            )}
                        />
                    )}
                </div>

                <button
                    type='button'
                    onClick={() => go(1)}
                    tabIndex={isOpen ? 0 : -1}
                    aria-label='Next image'
                    data-lightbox-chrome=''
                    className='absolute right-2 z-10 flex size-11 cursor-pointer items-center justify-center rounded-full border border-stone-line bg-cream/80 text-ink transition-colors duration-400 ease-nuvea hover:border-ink hover:bg-ink hover:text-cream md:right-4'
                >
                    <ArrowRight className='size-4' strokeWidth={1.6} />
                </button>
            </div>

            <div data-lightbox-chrome='' className='shrink-0 pb-6'>
                <p className='type-micro shell-x mb-3 text-center text-slate-soft'>
                    {current?.alt}
                </p>

                <ul className='flex items-center justify-center gap-2'>
                    {images.map((image, thumbIndex) => (
                        <li key={image.src}>
                            <button
                                type='button'
                                onClick={() => onIndexChange(thumbIndex)}
                                tabIndex={isOpen ? 0 : -1}
                                aria-label={`Show image ${thumbIndex + 1}`}
                                aria-current={thumbIndex === index ? 'true' : undefined}
                                className={cn(
                                    'relative size-14 cursor-pointer overflow-hidden rounded-lg border bg-shell transition-[border-color,transform] duration-400 ease-nuvea hover:-translate-y-0.5 md:size-16',
                                    thumbIndex === index ? 'border-ink' : 'border-stone-line/60'
                                )}
                            >
                                <Image
                                    src={image.src}
                                    alt=''
                                    aria-hidden='true'
                                    fill
                                    sizes='64px'
                                    className={cn(
                                        image.fit === 'cover' ? 'object-cover' : 'object-contain p-1'
                                    )}
                                />
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    )
}
